/**
 * External dependencies
 */
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';

/**
 * Internal dependencies
 */
import Breadcrumbs from '@Components/Breadcrumbs';
import Loader from '@Components/Loader';
import { useJobRun } from '@Hooks/useJobRuns';
import type { JobStatus } from '@Types/jobRun';
import { formatRunDuration } from '../formatRunDuration';

const STATUS_VARIANTS: Record<JobStatus, 'default' | 'destructive' | 'secondary'> = {
  SUCCESS: 'default',
  FAILURE: 'destructive',
  PENDING: 'secondary',
};

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

function JsonSyntaxHighlight({ data }: { data: JsonValue }) {
  const json = JSON.stringify(data, null, 2);
  if (!json) return <span className="text-zinc-400">null</span>;

  const highlighted = json.replace(
    /("(?:\\.|[^"\\])*")\s*:|("(?:\\.|[^"\\])*")|(\b(?:true|false)\b)|(\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match, key, str, bool, nil, num) => {
      if (key) return `<span class="text-purple-400">${key}</span>:`;
      if (str) return `<span class="text-green-400">${str}</span>`;
      if (bool) return `<span class="text-yellow-400">${bool}</span>`;
      if (nil) return `<span class="text-red-400">${nil}</span>`;
      if (num) return `<span class="text-cyan-400">${num}</span>`;
      return match;
    }
  );

  return (
    <pre className="overflow-auto rounded-lg bg-zinc-950 p-4 text-sm leading-relaxed text-zinc-300">
      <code dangerouslySetInnerHTML={{ __html: highlighted }} />
    </pre>
  );
}

export default function JobRunDetails() {
  const { jobRunId } = useParams<{ jobRunId: string }>();
  const { data: jobRun, status } = useJobRun({ id: jobRunId });

  if (status === 'pending') {
    return <Loader size="lg" />;
  }

  if (!jobRun) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        Job run not found.
      </div>
    );
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/job-runs' },
          { name: 'Job Runs', href: '/job-runs' },
          { name: jobRun.type, href: `/job-runs/type/${encodeURIComponent(jobRun.type)}` },
          { name: jobRun.id.slice(0, 8), href: `/job-runs/${jobRun.id}` },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center gap-3">
        <Heading>{jobRun.type}</Heading>
        <Badge variant={STATUS_VARIANTS[jobRun.status]}>
          {jobRun.status}
        </Badge>
      </div>

      <div className="mb-6 space-y-1 text-sm text-zinc-500">
        <div>
          {new Date(jobRun.createdAt).toLocaleString()}
          {jobRun.status !== 'PENDING' && ` · took ${formatRunDuration(jobRun.results?.durationSeconds)}`}
        </div>
        <div>
          Log span <code className="select-all rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-xs text-zinc-700">{jobRun.id}</code>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Results</CardTitle>
        </CardHeader>
        <CardContent>
          <JsonSyntaxHighlight data={jobRun.results} />
        </CardContent>
      </Card>
    </div>
  );
}
